import { Field, InvoiceFields } from 'components/DocumentDetail/types'
import applyErrorMsg from 'utils/applyErrorMsg'

import { FIELDS } from './DocumentsInvoiceBase'
import { DocumentInvoiceV2 } from './DocumentsInvoiceV2'

export function setExpenseTypeOptions(
  options: { label: string; value: string }[],
) {
  FIELDS.itemLine.find(
    ({ name }) => name === 'expenseTypeCode',
  )!.compProps.options = options
}

// general fields generator
function genSectionFields(
  sectionName: string,
  doc: DocumentInvoiceV2,
  { useConcur },
): Field[] {
  const { docInfo, ocrResp, config, isNew } = doc

  return (
    FIELDS[sectionName]
      // check visibility
      .filter((field) => config?.[sectionName]?.[field.name])
      // update name & inject detectedPage to each field
      ?.map((field) => {
        const disabled =
          !config?.[sectionName]?.[field.name]?.edit ||
          field?.compProps?.disabled
        let required =
          field.rules?.required && !useConcur ? false : field?.rules?.required
        // verifyの場合
        if (
          docInfo.isVerify &&
          !isNew &&
          !config?.[sectionName]?.[field.name]?.verify
        ) {
          required = false
        }
        return {
          ...field,
          label: doc.getLabel(sectionName, field.name),
          name: `${sectionName}.${field.name}`,
          confidence: ocrResp[sectionName]?.[field.name]?.confidenceScore,
          rules: applyErrorMsg({
            ...field?.rules,
            required,
          }),
          compProps: { ...field?.compProps, disabled },
          config: config?.[sectionName]?.[field.name],
        }
      })
  )
}

function genItemDefaultField(doc: DocumentInvoiceV2, { useConcur }): Field[] {
  const { config, docInfo } = doc
  const { isNew } = doc

  const sectionName = 'itemLines' // itemLines vs itemLine

  return (
    FIELDS.itemLine
      // check visibility
      .filter((field) => config?.[sectionName]?.[field.name])
      // update name & inject detectedPage to each field
      .map((field) => {
        const disabled =
          !config?.[sectionName]?.[field.name]?.edit ||
          field?.compProps?.disabled
        let required =
          field.rules?.required && !useConcur ? false : field?.rules?.required
        // verifyの場合
        if (
          docInfo.isVerify &&
          !isNew &&
          !config?.[sectionName]?.[field.name]?.verify
        ) {
          required = false
        }
        return {
          ...field,
          id: undefined,
          label: doc.getLabel(sectionName, field.name),
          confidence: undefined,
          rules: applyErrorMsg({
            ...field?.rules,
            required,
          }),
          compProps: { ...field?.compProps, disabled },
          config: config?.[sectionName]?.[field.name],
        }
      })
  )
}

// items fields generator
function genItemFields(doc: DocumentInvoiceV2): [string, ...Field[]][] | [] {
  const { config, ocrResp } = doc

  if (!ocrResp.itemLines) {
    return []
  }
  const sectionName = 'itemLines'

  return ocrResp.itemLines.map((ocrItemLine) => {
    return [
      ocrItemLine.id,
      ...FIELDS.itemLine
        // check visibility
        .filter((field) => config?.[sectionName]?.[field.name])
        // update name
        .map((field) => {
          const disabled =
            config?.itemLines?.[field.name].edit === false ||
            field?.compProps?.disabled

          return {
            ...field,
            id: ocrItemLine.id,
            order: ocrItemLine.order,
            label: doc.getLabel(sectionName, field.name),
            confidence: ocrItemLine[field.name]?.confidenceScore,
            rules: applyErrorMsg(field.rules),
            compProps: { ...field?.compProps, disabled },
            config: config?.itemLines?.[field.name],
          }
        }),
    ]
  })
}

export default function getInvoiceFields(
  doc: DocumentInvoiceV2,
  userGroupConfig,
  expenseTypeOptions: { label: string; value: string }[],
): InvoiceFields {
  setExpenseTypeOptions(expenseTypeOptions) // use cache?
  return {
    headerBase: genSectionFields('headerBase', doc, userGroupConfig),
    headerBillFrom: genSectionFields('headerBillFrom', doc, userGroupConfig),
    headerBillTo: genSectionFields('headerBillTo', doc, userGroupConfig),
    headerBank: genSectionFields('headerBank', doc, userGroupConfig),
    headerContact: genSectionFields('headerContact', doc, userGroupConfig),
    other: genSectionFields('other', doc, userGroupConfig),
    itemLines: genItemFields(doc /*, userGroupConfig */),
    itemDefaultField: genItemDefaultField(doc, userGroupConfig),
  }
}
